import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  Pagination,
} from '@mui/material';
import {
  Pending,
  CheckCircle,
  Cancel,
  Person,
  Email,
  Star,
  Visibility,
  ShoppingBag,
  Link,
} from '@mui/icons-material';
import { AdminReview, ReviewModerate } from '../types';
import { adminAPI } from '../services/api';
import RatingDisplay from '../components/RatingDisplay';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [pendingReviews, setPendingReviews] = useState<AdminReview[]>([]);
  const [approvedReviews, setApprovedReviews] = useState<AdminReview[]>([]);
  const [rejectedReviews, setRejectedReviews] = useState<AdminReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [moderationDialog, setModerationDialog] = useState<{
    open: boolean;
    review: AdminReview | null;
    action: 'approved' | 'rejected' | null;
  }>({
    open: false,
    review: null,
    action: null,
  });
  const [reviewDetailDialog, setReviewDetailDialog] = useState<{
    open: boolean;
    review: AdminReview | null;
  }>({
    open: false,
    review: null,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    user_name: '',
    user_email: '',
    rating: 5,
    advantages: '',
    disadvantages: '',
    comment: '',
  });
  const [moderatorComment, setModeratorComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const [pending, approved, rejected] = await Promise.all([
        adminAPI.getReviews('pending', 1, 50),
        adminAPI.getReviews('approved', 1, 50),
        adminAPI.getReviews('rejected', 1, 50),
      ]);
      
      setPendingReviews(pending);
      setApprovedReviews(approved);
      setRejectedReviews(rejected);
    } catch (err: any) {
      setError('Ошибка загрузки отзывов');
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = (review: AdminReview, action: 'approved' | 'rejected') => {
    setModerationDialog({
      open: true,
      review,
      action,
    });
    setModeratorComment('');
  };

  const handleViewDetails = (review: AdminReview) => {
    setEditForm({
      user_name: review.user_name,
      user_email: review.user_email,
      rating: review.rating,
      advantages: review.advantages || '',
      disadvantages: review.disadvantages || '',
      comment: review.comment,
    });
    setIsEditing(false);
    setReviewDetailDialog({
      open: true,
      review,
    });
  };

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleDeletePhoto = async (reviewId: string, photoId: string) => {
    try {
      await adminAPI.deletePhoto(reviewId, photoId);
      await loadReviews();
      // Обновляем детальный диалог если он открыт
      if (reviewDetailDialog.review && reviewDetailDialog.review.id === reviewId) {
        const updatedReview = { ...reviewDetailDialog.review };
        updatedReview.photos = updatedReview.photos.filter(photo => photo.id !== photoId);
        setReviewDetailDialog({ ...reviewDetailDialog, review: updatedReview });
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const confirmModeration = async () => {
    if (!moderationDialog.review || !moderationDialog.action) return;

    try {
      setSubmitting(true);
      
      // Если одобряем и есть изменения - сначала сохраняем изменения
      if (moderationDialog.action === 'approved' && isEditing) {
        await adminAPI.updateReview(moderationDialog.review.id, editForm);
      }
      
      const moderation: ReviewModerate = {
        status: moderationDialog.action,
        moderator_comment: moderatorComment || undefined,
      };

      await adminAPI.moderateReview(moderationDialog.review.id, moderation);
      
      // Обновляем состояние локально
      const updatedReview = {
        ...moderationDialog.review,
        status: moderationDialog.action,
        moderator_comment: moderatorComment,
      };

      // Удаляем из pending
      setPendingReviews(prev => prev.filter(r => r.id !== moderationDialog.review!.id));
      
      // Добавляем в соответствующий список
      if (moderationDialog.action === 'approved') {
        setApprovedReviews(prev => [updatedReview, ...prev]);
      } else {
        setRejectedReviews(prev => [updatedReview, ...prev]);
      }

      setModerationDialog({ open: false, review: null, action: null });
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка модерации отзыва');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Pending />;
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Cancel />;
      default: return <Pending />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'На модерации';
      case 'approved': return 'Одобрен';
      case 'rejected': return 'Отклонен';
      default: return status;
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    const confirmed = window.confirm('Удалить этот отзыв? Действие необратимо.');
    if (!confirmed) return;
    try {
      setSubmitting(true);
      await adminAPI.deleteReview(reviewId);
      setPendingReviews(prev => prev.filter(r => r.id !== reviewId));
      setApprovedReviews(prev => prev.filter(r => r.id !== reviewId));
      setRejectedReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка удаления отзыва');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const ReviewCard: React.FC<{ review: AdminReview; showActions?: boolean }> = ({ 
    review, 
    showActions = false 
  }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Информация о продукте */}
        {review.product_name && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ShoppingBag color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Товар: {review.product_name}
              </Typography>
            </Box>
            {review.product_description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {review.product_description.length > 100 
                  ? `${review.product_description.substring(0, 100)}...` 
                  : review.product_description
                }
              </Typography>
            )}
            {review.product_price && (
              <Typography variant="body2" fontWeight={600} color="primary.main">
                Цена: {review.product_price.toLocaleString('ru-RU')} ₽
              </Typography>
            )}
            <Button
              size="small"
              startIcon={<Link />}
              onClick={() => navigate(`/products/${review.product_id}/reviews`)}
              sx={{ mt: 1 }}
            >
              Перейти к товару
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6">{review.user_name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {review.user_email}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {formatDate(review.created_at)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <RatingDisplay rating={review.rating} showLabel={false} />
            <Chip
              icon={getStatusIcon(review.status)}
              label={getStatusText(review.status)}
              color={getStatusColor(review.status) as any}
              size="small"
            />
          </Box>
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {review.comment.length > 150 
            ? `${review.comment.substring(0, 150)}...` 
            : review.comment
          }
        </Typography>

        {review.advantages && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="success.main">
              Достоинства:
            </Typography>
            <Typography variant="body2">
              {review.advantages.length > 100 
                ? `${review.advantages.substring(0, 100)}...` 
                : review.advantages
              }
            </Typography>
          </Box>
        )}

        {review.disadvantages && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="error.main">
              Недостатки:
            </Typography>
            <Typography variant="body2">
              {review.disadvantages.length > 100 
                ? `${review.disadvantages.substring(0, 100)}...` 
                : review.disadvantages
              }
            </Typography>
          </Box>
        )}

        {review.photos && review.photos.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Фотографии: {review.photos.length}
            </Typography>
          </Box>
        )}

        {review.moderator_comment && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Комментарий модератора:
            </Typography>
            <Typography variant="body2">{review.moderator_comment}</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => handleViewDetails(review)}
              size="small"
            >
              Подробнее
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            {showActions && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => handleModerate(review, 'approved')}
                  size="small"
                >
                  Одобрить
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => handleModerate(review, 'rejected')}
                  size="small"
                >
                  Отклонить
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDeleteReview(review.id)}
              size="small"
            >
              Удалить
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Модерация отзывов
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Statistics */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 300px' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Pending sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {pendingReviews.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                На модерации
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 300px' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {approvedReviews.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Одобрено
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 300px' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Cancel sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" color="error.main">
                {rejectedReviews.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Отклонено
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab 
              label={`На модерации (${pendingReviews.length})`} 
              icon={<Pending />}
              iconPosition="start"
            />
            <Tab 
              label={`Одобренные (${approvedReviews.length})`} 
              icon={<CheckCircle />}
              iconPosition="start"
            />
            <Tab 
              label={`Отклоненные (${rejectedReviews.length})`} 
              icon={<Cancel />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {pendingReviews.length > 0 ? (
            pendingReviews.map((review) => (
              <ReviewCard key={review.id} review={review} showActions />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
              Нет отзывов на модерации
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {approvedReviews.length > 0 ? (
            approvedReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
              Нет одобренных отзывов
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {rejectedReviews.length > 0 ? (
            rejectedReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
              Нет отклоненных отзывов
            </Typography>
          )}
        </TabPanel>

        {/* Moderation Dialog */}
        <Dialog 
          open={moderationDialog.open} 
          onClose={() => setModerationDialog({ open: false, review: null, action: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {moderationDialog.action === 'approved' ? 'Одобрить отзыв' : 'Отклонить отзыв'}
          </DialogTitle>
          <DialogContent>
            {moderationDialog.review && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Отзыв от: {moderationDialog.review.user_name}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {moderationDialog.review.comment}
                </Typography>
              </Box>
            )}
            <TextField
              label="Комментарий модератора (необязательно)"
              fullWidth
              multiline
              rows={3}
              value={moderatorComment}
              onChange={(e) => setModeratorComment(e.target.value)}
              placeholder={
                moderationDialog.action === 'approved' 
                  ? 'Добавьте комментарий к одобрению...'
                  : 'Укажите причину отклонения...'
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModerationDialog({ open: false, review: null, action: null })}>
              Отмена
            </Button>
            <Button
              onClick={confirmModeration}
              variant="contained"
              color={moderationDialog.action === 'approved' ? 'success' : 'error'}
              disabled={submitting}
            >
              {submitting ? 'Обработка...' : 'Подтвердить'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Review Detail Dialog */}
        <Dialog 
          open={reviewDetailDialog.open} 
          onClose={() => setReviewDetailDialog({ open: false, review: null })}
          maxWidth="md"
          fullWidth
        >
        <DialogTitle>
          Детальный просмотр отзыва
          {reviewDetailDialog.review?.status === 'pending' && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                ✏️ Разрешено редактировать
              </Typography>
              <Button
                variant={isEditing ? "contained" : "outlined"}
                size="small"
                onClick={handleToggleEdit}
              >
                {isEditing ? "Отменить редактирование" : "Редактировать"}
              </Button>
            </Box>
          )}
        </DialogTitle>
          <DialogContent>
            {reviewDetailDialog.review && (
              <Box>
                {/* Product Info */}
                {reviewDetailDialog.review.product_name && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ShoppingBag color="primary" />
                      <Typography variant="h6" fontWeight={600}>
                        Товар: {reviewDetailDialog.review.product_name}
                      </Typography>
                    </Box>
                    {reviewDetailDialog.review.product_description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {reviewDetailDialog.review.product_description}
                      </Typography>
                    )}
                    {reviewDetailDialog.review.product_price && (
                      <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 1 }}>
                        Цена: {reviewDetailDialog.review.product_price.toLocaleString('ru-RU')} ₽
                      </Typography>
                    )}
                    <Button
                      size="small"
                      startIcon={<Link />}
                      onClick={() => reviewDetailDialog.review && navigate(`/products/${reviewDetailDialog.review.product_id}/reviews`)}
                    >
                      Перейти к товару
                    </Button>
                  </Box>
                )}

                {/* User Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                    <Person fontSize="large" />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          label="Имя пользователя"
                          value={editForm.user_name}
                          onChange={(e) => setEditForm({ ...editForm, user_name: e.target.value })}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Email пользователя"
                          value={editForm.user_email}
                          onChange={(e) => setEditForm({ ...editForm, user_email: e.target.value })}
                          size="small"
                          fullWidth
                        />
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="h6">{reviewDetailDialog.review.user_name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {reviewDetailDialog.review.user_email}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(reviewDetailDialog.review.created_at)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Рейтинг:</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <Button
                              key={rating}
                              variant={editForm.rating === rating ? 'contained' : 'outlined'}
                              onClick={() => setEditForm({ ...editForm, rating })}
                              size="small"
                              sx={{ minWidth: 32, height: 32 }}
                            >
                              {rating}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <RatingDisplay rating={reviewDetailDialog.review.rating} showLabel={true} />
                    )}
                    <Chip
                      icon={getStatusIcon(reviewDetailDialog.review.status)}
                      label={getStatusText(reviewDetailDialog.review.status)}
                      color={getStatusColor(reviewDetailDialog.review.status) as any}
                    />
                  </Box>
                </Box>

                {/* Review Content */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Комментарий:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      value={editForm.comment}
                      onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                      multiline
                      rows={4}
                      fullWidth
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1" sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {reviewDetailDialog.review.comment}
                    </Typography>
                  )}
                </Box>

                {/* Advantages */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Достоинства:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      value={editForm.advantages}
                      onChange={(e) => setEditForm({ ...editForm, advantages: e.target.value })}
                      multiline
                      rows={2}
                      fullWidth
                      variant="outlined"
                      placeholder="Введите достоинства..."
                    />
                  ) : (
                    <Typography variant="body1" sx={{ 
                      p: 2, 
                      bgcolor: 'success.50', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.200'
                    }}>
                      {reviewDetailDialog.review.advantages || 'Не указано'}
                    </Typography>
                  )}
                </Box>

                {/* Disadvantages */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="error.main" gutterBottom>
                    Недостатки:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      value={editForm.disadvantages}
                      onChange={(e) => setEditForm({ ...editForm, disadvantages: e.target.value })}
                      multiline
                      rows={2}
                      fullWidth
                      variant="outlined"
                      placeholder="Введите недостатки..."
                    />
                  ) : (
                    <Typography variant="body1" sx={{ 
                      p: 2, 
                      bgcolor: 'error.50', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'error.200'
                    }}>
                      {reviewDetailDialog.review.disadvantages || 'Не указано'}
                    </Typography>
                  )}
                </Box>

                {/* Photos */}
                {reviewDetailDialog.review.photos && reviewDetailDialog.review.photos.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Фотографии ({reviewDetailDialog.review.photos.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {reviewDetailDialog.review.photos.map((photo, index) => (
                        <Box key={index} sx={{ position: 'relative' }}>
                          <img
                            src={`${API_BASE_URL}${photo.thumb_url}`}
                            alt={`Фото ${index + 1}`}
                            style={{
                              width: 150,
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 8,
                              cursor: 'pointer',
                              border: '2px solid #e0e0e0'
                            }}
                            onClick={() => window.open(`${API_BASE_URL}${photo.full_url}`, '_blank')}
                          />
                          <Button
                            size="small"
                            color="error"
                            variant="contained"
                            sx={{ 
                              position: 'absolute', 
                              top: 4, 
                              right: 4, 
                              minWidth: 'auto',
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              p: 0
                            }}
                            onClick={() => handleDeletePhoto(reviewDetailDialog.review!.id, photo.id)}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Moderator Comment */}
                {reviewDetailDialog.review.moderator_comment && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Комментарий модератора:
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      p: 2, 
                      bgcolor: 'grey.100', 
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300'
                    }}>
                      {reviewDetailDialog.review.moderator_comment}
                    </Typography>
                  </Box>
                )}

                {/* Additional Info */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Дополнительная информация:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID отзыва: {reviewDetailDialog.review.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID товара: {reviewDetailDialog.review.product_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Обновлен: {formatDate(reviewDetailDialog.review.updated_at)}
                  </Typography>
                  {reviewDetailDialog.review.moderated_at && (
                    <Typography variant="body2" color="text.secondary">
                      Модерирован: {formatDate(reviewDetailDialog.review.moderated_at)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReviewDetailDialog({ open: false, review: null })}>
              Закрыть
            </Button>
            {reviewDetailDialog.review && reviewDetailDialog.review.status === 'pending' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => {
                    setReviewDetailDialog({ open: false, review: null });
                    handleModerate(reviewDetailDialog.review!, 'approved');
                  }}
                >
                  Одобрить
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => {
                    setReviewDetailDialog({ open: false, review: null });
                    handleModerate(reviewDetailDialog.review!, 'rejected');
                  }}
                >
                  Отклонить
                </Button>
              </Box>
            )}
          </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
};

export default AdminPage;
